// Dictionary plumbing shared by every product.
//
// A product's dictionary is the platform's (./en, ./pt) with the product's own
// sections merged on top and `{brand}` filled in:
//
//   const DICTIONARIES = productDictionaries('Mudafolio', { en: productEn, pt: productPt })
//
// The result is typed CoreStrings & ProductStrings, so the shared shell and
// the product's screens read the same object.

import type { Locale } from '@quoter/shared/locale'
import { coreEn, type CoreStrings, type Widen } from './en'
import { corePt } from './pt'

export type { CoreStrings, Widen }
export { coreEn, corePt }

export const CORE_DICTIONARIES: Record<Locale, CoreStrings> = { en: coreEn, pt: corePt }

type Tree = { readonly [key: string]: string | Tree }

function isTree(value: unknown): value is Tree {
  return typeof value === 'object' && value !== null
}

/** Deep merge; on a clash the second dictionary wins, so a product can override a platform line. */
export function mergeDictionaries<A extends Tree, B extends Tree>(base: A, extra: B): A & B {
  const out: Record<string, string | Tree> = { ...base }
  for (const [key, value] of Object.entries(extra)) {
    const current = out[key]
    out[key] = isTree(current) && isTree(value) ? mergeDictionaries(current, value) : value
  }
  return out as A & B
}

/** Fill `{brand}` in every string. Other placeholders are left for fmt() at render time. */
export function withBrand<T extends Tree>(dict: T, brand: string): T {
  const out: Record<string, string | Tree> = {}
  for (const [key, value] of Object.entries(dict)) {
    out[key] = isTree(value) ? withBrand(value, brand) : value.replaceAll('{brand}', brand)
  }
  return out as T
}

/** The platform dictionaries with a product's sections merged in, per locale. */
export function productDictionaries<P extends Tree>(
  brand: string,
  product: Record<Locale, P>,
): Record<Locale, CoreStrings & P> {
  return {
    en: withBrand(mergeDictionaries(coreEn as CoreStrings, product.en), brand),
    pt: withBrand(mergeDictionaries(corePt, product.pt), brand),
  }
}
